import * as React from 'react';
import { Button, ModalWindow, Spinner } from '@cian/ui-kit';

import {
  ManifestationType,
  TotalAreaInput,
  KitchenAreaInput,
  LivingAreaInput,
  ApartmentType,
  PenthouseType,
  HouseType,
  NewbuildingClass,
  UndergroundWalkTimeInput,
  HasDecoration,
  ConstructionStatus,
  HasMortgage,
  SaleType,
  IsSalesStart,
  IsSalesLeader,
  IsBlackFriday,
  HasDiscountMortgage,
} from '../../Filters';
import { Container } from '../../../components/Container';
import {
  SIZE_0,
  SIZE_8,
  SIZE_12,
  SIZE_24,
  SIZE_28,
  SIZE_192,
  SIZE_WIDE,
  SIZE_440,
  SIZE_100,
  SIZE_20,
} from '../../../constants/sizes';
import { ADFOX_TIMEOUT, MORE_FILTERS_BANNER_SIZE, EAdFoxBannerType } from '../../../constants/adfox';
import { renderFieldRow } from './parts/renderFieldRow';
import { renderTitle } from './parts/renderTitle';
import { SearchButton } from '../../../components/SearchButton';
import { AdfoxContainer } from '../../AdfoxContainer';

export interface IModalStoreProps {
  count: number | null;
  isCountFetching?: boolean;
  hasUndreground: boolean;
  canUseHiddenBase: boolean;
  isBlackFridayActive: boolean;
  discountMortgageEnabled: boolean;
}

export interface IModalDispatchProps {
  onSubmit(canUseHiddenBase: boolean): void;
  trackingPopupMoreParamOpen(): void;
  updateTags(): void;
}

interface IModalState {
  isOpen: boolean | undefined;
  bannerLoaded: boolean;
  bannerError: boolean;
}

type TModalProps = IModalStoreProps & IModalDispatchProps;

export class MoreFiltersModalComponent extends React.PureComponent<TModalProps, IModalState> {
  public state: Readonly<IModalState> = {
    isOpen: false,
    bannerLoaded: false,
    bannerError: false,
  };
  private adTimer: number;

  public componentWillUnmount() {
    window.clearTimeout(this.adTimer);
  }

  public render() {
    const { count, isCountFetching, hasUndreground, isBlackFridayActive, discountMortgageEnabled } = this.props;
    const { isOpen, bannerLoaded, bannerError } = this.state;

    return (
      <>
        <Button size="XS" tabIndex={-1} theme="stroke_secondary" type="button" onClick={() => this.handleToggle(true)}>
          Ещё фильтры
        </Button>
        <ModalWindow
          open={isOpen}
          onClose={() => this.handleToggle(false)}
          fixed={true}
          footer={
            <Container padding={SIZE_0} display="flex" justifyContent="center" alignItems="center">
              <SearchButton count={count} loading={isCountFetching} onClick={this.handleSubmit} />
            </Container>
          }
          content={
            <>
              {!bannerLoaded && (
                <Container
                  display="flex"
                  justifyContent="center"
                  width="calc(100% - 80px)"
                  position="absolute"
                  top="calc(50% - 20px)"
                >
                  <Spinner color="primary_100" size={40} />
                </Container>
              )}
              <Container
                display="flex"
                visibility={bannerLoaded ? 'visible' : 'hidden'}
                flexDirection="column"
                justifyContent="space-between"
                height={SIZE_WIDE}
              >
                <Container padding={SIZE_0} overflow="auto">
                  {!bannerError && (
                    <Container margin={`0 ${SIZE_28} ${SIZE_12}`} width={SIZE_440} height={SIZE_100}>
                      <AdfoxContainer
                        onError={this.handleAdfoxError}
                        onLoad={this.handleAdfoxMount}
                        type={EAdFoxBannerType.PICTURE}
                        size={MORE_FILTERS_BANNER_SIZE}
                      />
                    </Container>
                  )}
                  {renderFieldRow({
                    title: 'Тип объявления',
                    field: <ManifestationType />,
                  })}
                  {renderFieldRow({
                    title: 'Тип продажи',
                    field: (
                      <Container display="flex">
                        <SaleType />
                        <Container margin={`0 0 0 ${SIZE_20}`} alignSelf="center">
                          <HasMortgage />
                        </Container>
                      </Container>
                    ),
                  })}
                  {renderFieldRow({
                    title: 'Подборки ЖК',
                    field: (
                      <Container display="flex">
                        {isBlackFridayActive && (
                          <Container margin={`0 ${SIZE_8} 0 0`}>
                            <IsBlackFriday />
                          </Container>
                        )}
                        <Container margin={`0 ${SIZE_8} 0 0`}>
                          <IsSalesStart />
                        </Container>
                        <Container margin={`0 ${SIZE_8} 0 0`}>
                          <IsSalesLeader />
                        </Container>
                        {discountMortgageEnabled && (
                          <Container>
                            <HasDiscountMortgage />
                          </Container>
                        )}
                      </Container>
                    ),
                  })}
                  {renderTitle('О квартире')}
                  {renderFieldRow({
                    title: 'Площадь, м²',
                    field: (
                      <Container display="flex">
                        <Container width={SIZE_192}>
                          <TotalAreaInput />
                        </Container>
                        <Container width={SIZE_192} margin={`0 0 0 ${SIZE_24}`}>
                          <KitchenAreaInput />
                        </Container>
                        <Container width={SIZE_192} margin={`0 0 0 ${SIZE_24}`}>
                          <LivingAreaInput />
                        </Container>
                      </Container>
                    ),
                  })}
                  {renderFieldRow({
                    title: 'Отделка',
                    field: (
                      <Container display="flex">
                        <HasDecoration />
                      </Container>
                    ),
                  })}
                  {renderTitle('О жилом комплексе и корпусе')}
                  {renderFieldRow({
                    title: 'Тип дома',
                    field: <HouseType />,
                  })}
                  {renderFieldRow({
                    title: 'Класс жилого комплекса',
                    field: <NewbuildingClass />,
                  })}
                  {renderFieldRow({
                    title: 'Статус помещения',
                    field: (
                      <Container display="flex">
                        <ApartmentType />
                        <Container margin={`0 0 0 ${SIZE_8}`}>
                          <PenthouseType />
                        </Container>
                      </Container>
                    ),
                  })}
                  {hasUndreground &&
                    renderFieldRow({
                      title: 'До метро пешком',
                      field: (
                        <Container display="flex">
                          <UndergroundWalkTimeInput />
                        </Container>
                      ),
                    })}
                  {renderFieldRow({
                    title: 'Статус строительства',
                    field: (
                      <Container display="flex">
                        <ConstructionStatus />
                      </Container>
                    ),
                  })}
                </Container>
              </Container>
            </>
          }
        />
      </>
    );
  }

  private handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      this.props.trackingPopupMoreParamOpen();
      this.adTimer = window.setTimeout(this.handleAdfoxError, ADFOX_TIMEOUT);
    } else {
      this.props.updateTags(); //При закрытии модального окна обновляем тэги
    }
    this.setState({
      isOpen,
      bannerLoaded: false,
      bannerError: false,
    });
  };

  private handleSubmit = () => {
    this.props.onSubmit(this.props.canUseHiddenBase);
    this.setState({
      isOpen: false,
      bannerLoaded: false,
      bannerError: false,
    });
  };

  private handleAdfoxError = () => {
    this.setState({
      bannerLoaded: true,
      bannerError: true,
    });
  };

  private handleAdfoxMount = () => {
    window.clearTimeout(this.adTimer);
    this.setState({
      bannerLoaded: true,
      bannerError: false,
    });
  };
}
